import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";
import { create } from "zustand";

export type AttemptNavigation = (navigate: () => void) => void;

interface NavGuardState {
  /**
   * Published by whichever listing surface currently has unsaved edits
   * (the add-listing wizard or the edit-listing provider); `null` when nothing
   * is dirty. `GlobalNavGuard` reads it to decide whether to intercept NavBar /
   * footer link clicks, and `useGuardedNavigate` reads it for imperative
   * navigations — both delegate the actual navigation to it so each surface
   * owns its own prompt + history handling.
   */
  attemptNavigation: AttemptNavigation | null;
  /**
   * Identity of the surface that published the current handler. Used so a
   * stale clear from an unmounting surface can't clobber a handler a
   * freshly-mounted surface already published. See `usePublishNavGuard`.
   */
  owner: symbol | null;
  setAttemptNavigation: (owner: symbol, fn: AttemptNavigation | null) => void;
}

/**
 * A single global handoff point bridging the root-layout `GlobalNavGuard`
 * (which lives above the NavBar) with the deeply-nested listing surfaces that
 * track unsaved edits. Lets us prompt before NavBar navigation without touching
 * the NavBar or UserMenu components.
 */
export const useNavGuardStore = create<NavGuardState>((set) => ({
  attemptNavigation: null,
  owner: null,
  setAttemptNavigation: (owner, fn) =>
    set((state) => {
      // Publishes are last-writer-wins, but a *clear* only lands if you still
      // own the slot (or it's already empty). This keeps a stale clear from an
      // unmounting surface from wiping a handler that a newer surface just
      // published during a surface-to-surface transition.
      if (fn === null && state.owner !== null && state.owner !== owner) {
        return state;
      }
      return { attemptNavigation: fn, owner: fn === null ? null : owner };
    }),
}));

/**
 * Publish a surface's trap-aware navigator to the global guard while `active`,
 * and clear it on unmount. Carries a stable per-mount owner token so the clear
 * is a no-op once another surface has taken over the slot — see the store's
 * `setAttemptNavigation`. Both listing surfaces (add wizard + edit provider)
 * use this instead of touching the store directly.
 */
export function usePublishNavGuard(
  active: boolean,
  attemptNavigation: AttemptNavigation,
): void {
  // Lazily minted once; useRef keeps the same token across the mount's renders.
  const ownerRef = useRef<symbol | null>(null);
  if (ownerRef.current === null) ownerRef.current = Symbol("nav-guard-owner");

  useEffect(() => {
    const owner = ownerRef.current as symbol;
    useNavGuardStore
      .getState()
      .setAttemptNavigation(owner, active ? attemptNavigation : null);
  }, [active, attemptNavigation]);

  // Clear on unmount — owner-checked, so it won't clobber a successor surface.
  useEffect(() => {
    const owner = ownerRef.current as symbol;
    return () => useNavGuardStore.getState().setAttemptNavigation(owner, null);
  }, []);
}

export function useGuardedNavigate(): (href: string) => void {
  const router = useRouter();
  const attemptNavigation = useNavGuardStore((s) => s.attemptNavigation);

  return useCallback(
    (href: string) => {
      const navigate = () => router.push(href);
      if (attemptNavigation) attemptNavigation(navigate);
      else navigate();
    },
    [router, attemptNavigation],
  );
}
